import { CIActionBtnGroup, CIStatusCapsule, Loader } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RESPONSE_CODE } from '../../../../../common/constants/StatusConstants';
import { URL_PATH } from '../../../../constants';
import useServiceDependencyGraph from '../../../../hooks/services/useServiceDependencyGraph';
import { IActiveJourney, IJourneyData } from '../../../../store/actions/types/sources/GetAllJourneys';
import AssociationList from '../../../events/events-associations/association-list/AssociationList';
import classes from './JourneyAssociationList.module.scss';

const DEFAULT_SORT_FIELD = 'journeyName';
const DEFAULT_SORT_ORDER = 'DESC';
const SORT_ASCENDING = 'ASC';
const SORT_DESCENDING = 'DESC';

interface IJourneyAssociationListProps {
  queryParams: { [key: string]: string };
  setQueryParams: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onJourneyListSuccess: (journeyResponse: IActiveJourney) => void;
}

const CIGridLabels = {
  JOURNEY_NAME: 'journeyName',
  JOURNEY_STATUS: 'status',
};

const JourneyAssociationList = ({
  queryParams,
  setQueryParams,
  onJourneyListSuccess
}: IJourneyAssociationListProps) => {
  const location = useLocation();
  const history = useNavigate();
  const [journeys, setJourney] = useState<IActiveJourney>({
    totalJourneysCount: 0,
    journeyList: []
  });
  const { journeyList } = journeys;
  const { t: translate } = useTranslation();
  const { getServiceDependencyGraph } = useServiceDependencyGraph();
  const [page, setPage] = useState<number>(0);
  const [item, setItem] = useState<number>(10);
  const [isListLoading, setIsListLoading] = useState(false);
  const { associationServiceName, associationVersion } = location.state;
  const [selectedSortField, setSelectedSortField] = useState(
    queryParams.sort ?? DEFAULT_SORT_FIELD
  );
  const [selectedSortFieldOrder, setSelectedSortFieldOrder] = useState<boolean>(
    queryParams?.sortType === DEFAULT_SORT_ORDER
  );

  /**
   * @method renderServiceNameAndVersion
   * @description render journey name and version
   */
  const renderServiceNameAndVersion = (row: {
    journeyName: string;
    version: string;
  }) => (
    <div className={classes['service-name-wrapper']}>
      <p className={classes['service-name']}>{row.journeyName}</p>
      {row.version && (
        <p className={classes['service-version']}>{`Ver ${row.version}`}</p>
      )}
    </div>
  );

  /**
   * Renders the status capsule component based on the provided row status.
   * @param row - The row object containing the status property.
   * @returns The CIStatusCapsule component if the row status is truthy, otherwise null.
   */
  const renderStatus = (row: { status: string }) =>
    row.status && <CIStatusCapsule status={row.status} />;

  const redirectToViewJourney = (row: IJourneyData) => {
    history(`${URL_PATH.JOURNEY_PATH}/${row.journeyName}/${row.version}`);
  };
  /**
   * Renders a button group for a row in the service list.
   * @param row - The row object.
   * @returns The rendered button group.
   */
  const renderButtonGrp = (row: IJourneyData) => (
    <CIActionBtnGroup
      buttons={[
        {
          label: translate('info_message.lbl_journey_navigation'),
          key: 'viewIcon',
          onClick: () => redirectToViewJourney(row),
          testId: 'service-nav-button'
        }
      ]}
    />
  );

  /**
   * @method sortHandler
   * @description handles sort action
   */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre: { [key: string]: string }) => ({
      ...pre,
      sortColumn: label,
      sortType: selectedLabelState ? SORT_DESCENDING : SORT_ASCENDING
    }));
  };

  /**
   * @method handleClick
   * @description Handles the click event on the element.
   * @param e - The click event.
   * @param label - The label associated with the clicked element.
   */
  const handleSortClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };
  const headers = [
    {
      label: translate('journey_list_component.lbl_journey_name'),
      name: CIGridLabels.JOURNEY_NAME,
      options: { breakPointWidth: 6 },
      columnData: { field: '', render: renderServiceNameAndVersion },
      sortOptions: {
        callback: handleSortClick
      }
    },
    {
      label: translate('journey_list_component.lbl_status'),
      name: CIGridLabels.JOURNEY_STATUS,
      options: { breakPointWidth: 5 },
      columnData: { field: '', render: renderStatus },
      sortOptions: {
        callback: handleSortClick
      }
    },
    {
      name: 'journey-redirection',
      options: { breakPointWidth: 1 },
      columnData: { field: '', render: renderButtonGrp }
    }
  ];

  /**
   * @method onGetPageHandler
   * @description Handles the event when a page number is selected.
   * @param {number} pageNumber - The selected page number.
   */
  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    setQueryParams((pre) => ({
      ...pre,
      page: String(pageNumber)
    }));
  };

  /**
   * @method onGetItemsHandler
   * @description Handles the event when an item is selected.
   * @param itemNumber - The number of the selected item.
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber)
    }));
  };

  const sortedObj = {
    sortedName: selectedSortField,
    sortedOrder: selectedSortFieldOrder ? SORT_DESCENDING : SORT_ASCENDING
  };

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: item
      ? Math.ceil(journeys.totalJourneysCount / Number(item))
      : 1,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  const getAllJourneys = async () => {
    setIsListLoading(true);
    try {
      const { statusCode, graphResponse } = await getServiceDependencyGraph(
        `${associationServiceName}`,
        `${associationVersion}`,
        `${page}`,
        item,
        `${selectedSortField},${queryParams?.sortType}`,
        queryParams?.status?.split(','),
        queryParams?.searchText
      );
      if (
        statusCode === RESPONSE_CODE.POST_SUCCESS
        && graphResponse?.totalJourneysCount
        && graphResponse?.totalJourneysCount > 0
      ) {
        setJourney(graphResponse);
        onJourneyListSuccess(graphResponse);
        setIsListLoading(false);
      } else {
        setJourney({ totalJourneysCount: 0, journeyList: [] });
        onJourneyListSuccess({ totalJourneysCount: 0, journeyList: [] });
        setIsListLoading(false);
      }
    } catch (exception) {
      onJourneyListSuccess({ totalJourneysCount: 0, journeyList: [] });
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    getAllJourneys();
  }, [page, item, queryParams?.status, queryParams?.sortType, queryParams?.searchText]);

  return (
    <div className={classes['service-list-container']}>
      <Loader show={isListLoading} />
      <AssociationList
        headers={headers}
        tableData={journeyList}
        sorted={sortedObj}
        footer={footerObj}
      />
    </div>
  );
};

export default JourneyAssociationList;
